/**
 * Richieste di aiuto agli amministratori (ADR 0032 §6), servite da `agents`.
 *
 * L'invio è di ogni utente (al più cinque in 24 ore): la richiesta si salva e
 * l'avviso arriva agli amministratori sul canale Telegram degli allarmi. Lettura e
 * chiusura sono solo dell'`ADMIN`, dalla scheda «Richieste di aiuto» di
 * Amministrazione.
 *
 * Provvisorio dichiarato, come gli altri client. `support.test.ts` presidia metodo,
 * percorso e corpo.
 */

import type { ApiClient } from './client';

/** Argomento di una richiesta. */
export type SupportCategory = 'question' | 'problem' | 'billing' | 'other';

/** Argomenti, nell'ordine delle scelte. */
export const SUPPORT_CATEGORIES: readonly SupportCategory[] = ['question', 'problem', 'billing', 'other'];

/** Stato di una richiesta. */
export type SupportStatus = 'open' | 'closed';

/** Una richiesta da inviare. */
export interface NewSupportRequest {
  /** Argomento. */
  category: SupportCategory;
  /** Oggetto, da 3 a 200 caratteri. */
  subject: string;
  /** Testo, da 10 a 4000 caratteri. */
  message: string;
  /** Pagina da cui si scrive. */
  page?: string;
}

/** Una richiesta salvata. */
export interface SupportRequest {
  /** Richiesta. */
  id: string;
  /** Chi ha scritto. */
  username: string;
  /** Argomento. */
  category: SupportCategory;
  /** Oggetto. */
  subject: string;
  /** Testo. */
  message: string;
  /** Pagina da cui ha scritto. */
  page: string;
  /** Stato. */
  status: SupportStatus;
  /** Invio, ISO 8601. */
  created_at: string;
  /** Chiusura, ISO 8601. */
  closed_at: string | null;
}

/** Una pagina delle richieste. */
export interface SupportPage {
  /** Richieste, dalla più recente. */
  items: SupportRequest[];
  /** Richieste che rispondono al filtro. */
  total: number;
}

/** Client delle richieste di aiuto. */
export class SupportApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Scrive agli amministratori.
   *
   * @param request Richiesta.
   * @returns La richiesta salvata.
   */
  create(request: NewSupportRequest): Promise<SupportRequest> {
    return this.client.request<SupportRequest>('POST', '/api/v1/support/requests', request);
  }

  /**
   * Una pagina delle richieste (solo `ADMIN`).
   *
   * @param status Solo questo stato; omesso, tutte.
   * @param offset Richieste da saltare.
   * @returns La pagina.
   */
  list(status?: SupportStatus, offset = 0): Promise<SupportPage> {
    const params = new URLSearchParams({ limit: '50' });
    if (status !== undefined) {
      params.set('status', status);
    }
    if (offset > 0) {
      params.set('offset', String(offset));
    }
    return this.client.request<SupportPage>('GET', `/api/v1/support/requests?${params.toString()}`);
  }

  /**
   * Chiude o riapre una richiesta (solo `ADMIN`).
   *
   * @param requestId Richiesta.
   * @param status Stato nuovo.
   * @returns La richiesta.
   */
  setStatus(requestId: string, status: SupportStatus): Promise<SupportRequest> {
    return this.client.request<SupportRequest>('PATCH', `/api/v1/support/requests/${encodeURIComponent(requestId)}`, {
      status,
    });
  }
}
